import React from "react";
import moment from "moment";

import { GeneralNumbers } from "components/Dashboard";
import { HorizontalSquareGrid, HorizontalHistogram, Line, Pyramid } from "components/_charts";
import { findMinMax, formatNo } from "utils";
import { ACCIDENT_TYPE_ACCIDENT, ACCIDENT_TYPE_INCIDENT, ACCIDENT_TYPE_NEARMISS } from "./dbValues";
import { submissionService } from "services";

const WEEK_FORMAT = "YYYY-WW";

export function getXTimeSeries(format = "YYYY-MM-DD") {
  return (d, i, data) => moment(d.label, format).unix();
}

export function getXDomainTimeSeries(data, multiple = false) {
  const range = multiple ? [].concat.apply([], data) : data;
  return range.length === 1 ? [range[0].x - 1, range[0].x + 1] : findMinMax(range, "x");
}

export function allWeeksFromRange(range) {
  const [start, end] = range;
  const result = [];
  let current = start;
  while (current <= end) {
    result.push(current);
    current = moment.unix(current).add(1, "week").unix();
  }
  return result;
}

export function getYDomainTimeSeries(data, multiple = false) {
  const range = multiple ? [].concat.apply([], data) : data;
  return range.length === 1 ? [0, range[0].y] : findMinMax(range, "y");
}

export function transformObjectData(data) {
  if (!data || !data[0]) return data;
  return Object.keys(data[0]).map((key) =>
    data[0][key].map((d) => ({ label: d.date, value: d.value })),
  );
}

export const sections = [
  {
    title: "Général",
    charts: [
      {
        url: "/statistics/count/general",
        component: GeneralNumbers,
        height: "auto",
      },
    ],
  },
  {
    title: "Intervention SSE",
    charts: [
      {
        title: "Nombre d'interventions SSE par utilisateur par semaine",
        url: "/statistics/intervention_user?per_week=1",
        multiSeries: true,
        component: Line,
        height: "auto",
        transformData: function (data) {
          if (!data) return data;
          data = transformObjectData(data);
          const range = [].concat
            .apply([], data)
            .map((d) => ({ date: moment(d.label, WEEK_FORMAT).unix() }));
          const minMax = findMinMax(range, "date");
          const allWeeks = allWeeksFromRange(minMax).map((week) =>
            moment.unix(week).format(WEEK_FORMAT),
          );
          return data.map((series) =>
            allWeeks.map((week) => {
              const found = series.find((d) => d.label === week)?.value;
              return {
                label: week,
                size: found ? 4 : 0,
                value: found || 0,
              };
            }),
          );
        },
        transformLabels: function (data) {
          if (!data || !data[0]) return data;
          return Object.keys(data[0]);
        },
        props: {
          getX: getXTimeSeries(WEEK_FORMAT),
          getXDomain: (data) => getXDomainTimeSeries(data, true),
          getYDomain: (data) => getYDomainTimeSeries(data, true),
          getTickLabel: function (x) {
            return moment.unix(x).format(WEEK_FORMAT);
          },
          getTickValues: function (data) {
            return data && data[0] && data[0].map((d) => d.x);
          },
          getYTickLabel: (y) => y,
          animation: "",
          height: 400,
          minWidth: 800,
          hasMarks: true,
          margin: { bottom: 45 },
          xAxisLabel: "Semaine",
          xType: "time",
          legendOrientation: "horizontal",
          yAxisLabel: "Nombre d'interventions",
          crosshair: (crosshair) => {
            const date = (crosshair[0] || {}).x;
            const lines = [];
            for (let i = 1; i < crosshair.length; i++)
              lines.push(
                <p key={i}>
                  {crosshair[i].type} : {formatNo(crosshair[i].y || 0)}
                </p>,
              );
            return (
              <div className="vis-crosshair">
                <h4>{moment.unix(date).format(WEEK_FORMAT)}</h4>
                {lines}
              </div>
            );
          },
        },
      },
      {
        title: "Nombre d'interventions SSE par personne",
        url: "/statistics/intervention_user",
        component: HorizontalHistogram,
        maxHeight: 500,
        classes: "overflow",
      },
    ],
  },
  // {
  //   title: "Soumission",
  //   charts: [
  //     {
  //       title: "Nombre de soumissions par étape",
  //       fetchData: async (filters) => {
  //         if ("date" in filters) {
  //           filters["date_depot"] = filters["date"];
  //           delete filters["date"];
  //         }
  //         return await submissionService.listSubmissions(filters, filters, []);
  //       },
  //       component: HorizontalHistogram,
  //       maxHeight: 500,
  //       classes: "overflow",
  //       transformData: function (data) {
  //         try {
  //           if (!data) return data;
  //           const { states = [], submissions = [] } = data;

  //           const counts = states.map((state) => ({
  //             label: state.label,
  //             value: submissions.filter(
  //               (subm) => subm.submission_state_id === state.id
  //             ).length,
  //           }));

  //           return counts;
  //         } catch {
  //           return [];
  //         }
  //       },
  //       props: {
  //         legendOrientation: "horizontal",
  //         fontSizeRatio: {
  //           min: 1 / 3.5,
  //           max: 1 / 2.5,
  //         },
  //       },
  //     },
  //   ],
  // },
  {
    title: "Incident",
    subtitle: "Ces données ne sont pas impactées par les filtres plus hauts.",
    charts: [
      {
        title: "Type d'incident",
        url: "/statistics/count/incident",
        component: Pyramid,
        width: "5-10",
        height: "auto",
        transformData: function (data) {
          if (!data || !data.length) return data;
          return [
            {
              label: "Accident",
              value: data.find((count) => count.accident === ACCIDENT_TYPE_ACCIDENT)?.value || 0,
            },
            {
              label: "Incident",
              value: data.find((count) => count.accident === ACCIDENT_TYPE_INCIDENT)?.value || 0,
            },
            {
              label: "Near miss",
              value: data.find((count) => count.accident === ACCIDENT_TYPE_NEARMISS)?.value || 0,
            },
          ];
        },
        props: {
          legendOrientation: "horizontal",
          fontSizeRatio: {
            min: 1 / 3.5,
            max: 1 / 2.5,
          },
        },
      },
      {
        title: "Interventions SSE vs. Tailgates",
        url: "/statistics/count/tailgate",
        component: Pyramid,
        width: "5-10",
        height: "auto",
        transformData: function (data) {
          if (!data) return data;
          return [
            { label: "Interventions SSE", value: data.intervention_count },
            { label: "Tailgates", value: data.tailgate_count },
          ];
        },
        props: {
          inverted: true,
          legendOrientation: "horizontal",
          colorOffset: 3,
          fontSizeRatio: {
            min: 1 / 7,
            max: 1,
          },
        },
      },
      {
        title: "Risque impliqué",
        url: "/statistics/risk_accident",
        component: HorizontalSquareGrid,
        height: "auto",
        transformData: function (data) {
          if (!data) return data;
          return Object.keys(data).map((risk) => ({
            group: risk,
            values: [
              {
                label: "Accident",
                value: data[risk][ACCIDENT_TYPE_ACCIDENT] || 0,
              },
              {
                label: "Incident",
                value: data[risk][ACCIDENT_TYPE_INCIDENT] || 0,
              },
              {
                label: "Near miss",
                value: data[risk][ACCIDENT_TYPE_NEARMISS] || 0,
              },
            ],
          }));
        },
        props: {
          legendOrientation: "horizontal",
        },
      },
    ],
  },
];
